import { BarChart3, Home, Menu, Milk, PawPrint, Plus, Stethoscope, Wallet } from 'lucide-react';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useIsOwner, useSession } from '@/auth/useSession';
import { Sheet, Spinner, buttonVariants } from '@/components/ui';
import { cn } from '@/lib/cn';
import { SyncIndicator } from '@/sync/SyncIndicator';

/** Mobile bottom navigation with a floating record button; desktop (≥ 1024 px) sidebar + Reports. */
export function Layout() {
  const { t } = useTranslation();
  const session = useSession();
  const isOwner = useIsOwner();
  const navigate = useNavigate();
  const [plusOpen, setPlusOpen] = useState(false);
  const items = [
    { to: '/', label: t('nav.today'), icon: Home, end: true },
    { to: '/milk', label: t('nav.milk'), icon: Milk },
    { to: '/animals', label: t('nav.animals'), icon: PawPrint },
    { to: '/money', label: t('nav.money'), icon: Wallet },
  ];
  return (
    <div className="min-h-dvh lg:flex">
      <aside className="no-print hidden w-60 shrink-0 flex-col gap-1 border-r border-stone-200 bg-white p-3 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:self-start lg:overflow-y-auto lg:overscroll-contain">
        <div className="mb-4 flex items-center gap-2 px-2 pt-2">
          <img src="/icons/icon-192.png" alt="" className="h-9 w-9 rounded-xl" />
          <span className="truncate font-bold">{session.profile?.tenantName}</span>
        </div>
        {items.map((i) => (
          <SideLink key={i.to} {...i} />
        ))}
        <SideLink to="/events/new" label={t('nav.recordEvent')} icon={Stethoscope} />
        {isOwner ? <SideLink to="/dashboard" label={t('nav.reports')} icon={BarChart3} /> : null}
        <SideLink to="/more" label={t('nav.more')} icon={Menu} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-30 flex items-center gap-2 border-b border-stone-200 bg-white/95 px-4 py-2 backdrop-blur">
          <Link to="/" className="min-w-0 flex-1 truncate font-semibold lg:hidden">
            {session.profile?.tenantName ?? t('app.name')}
          </Link>
          <div className="hidden flex-1 lg:block" />
          <SyncIndicator />
        </header>
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 pb-[calc(10rem+env(safe-area-inset-bottom))] pt-4 lg:pb-10">
          <Suspense
            fallback={
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      <nav className="no-print safe-bottom fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-stone-200 bg-white lg:hidden">
        {items.map((i) => (
          <BottomLink key={i.to} {...i} />
        ))}
        <BottomLink to="/more" label={t('nav.more')} icon={Menu} />
      </nav>

      <button
        type="button"
        onClick={() => setPlusOpen(true)}
        aria-label={t('nav.record')}
        aria-haspopup="dialog"
        aria-expanded={plusOpen}
        className="no-print fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-30 flex h-14 w-14 items-center justify-center rounded-full bg-brand-800 text-white shadow-lg transition-colors hover:bg-brand-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-800 lg:hidden"
      >
        <Plus className="h-7 w-7" aria-hidden="true" />
      </button>

      <Sheet open={plusOpen} onOpenChange={setPlusOpen} title={t('nav.record')}>
        <div className="grid gap-3 pb-2">
          <button
            className={cn(buttonVariants({ size: 'lg' }), 'w-full')}
            onClick={() => {
              setPlusOpen(false);
              navigate('/milk/session');
            }}
          >
            <Milk className="h-6 w-6" /> {t('nav.recordMilk')}
          </button>
          <button
            className={cn(buttonVariants({ size: 'lg', variant: 'secondary' }), 'w-full')}
            onClick={() => {
              setPlusOpen(false);
              navigate('/events/new');
            }}
          >
            <Stethoscope className="h-6 w-6" /> {t('nav.recordEvent')}
          </button>
        </div>
      </Sheet>
    </div>
  );
}

type NavItem = { to: string; label: string; icon: typeof Home; end?: boolean };

function BottomLink({ to, label, icon: Icon, end }: NavItem) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn('flex min-h-16 flex-col items-center justify-center gap-0.5 text-xs font-medium', isActive ? 'text-brand-800' : 'text-stone-600')
      }
    >
      <Icon className="h-6 w-6" />
      <span className="max-w-full truncate px-1">{label}</span>
    </NavLink>
  );
}

function SideLink({ to, label, icon: Icon, end }: NavItem) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn('flex min-h-11 items-center gap-3 rounded-xl px-3 font-medium', isActive ? 'bg-brand-50 text-brand-900' : 'text-stone-700 hover:bg-stone-100')
      }
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  );
}
